import { useMemo, useState } from "react";
import { Search, Users } from "lucide-react";

export function buildCustomers(bookings, tickets) {
  const map = new Map();
  const get = (email, name, phone) => {
    const key = (email || "").toLowerCase();
    if (!key) return null;
    if (!map.has(key)) map.set(key, { email: key, name: name || "", phone: phone || "", bookings: [], tickets: [], last: "" });
    const c = map.get(key);
    if (!c.name && name) c.name = name;
    if (!c.phone && phone) c.phone = phone;
    return c;
  };
  bookings.forEach((b) => { const c = get(b.email, b.name, b.phone); if (c) { c.bookings.push(b); if (b.scheduled_at > c.last) c.last = b.scheduled_at; } });
  tickets.forEach((t) => { const c = get(t.email, t.name, t.phone); if (c) { c.tickets.push(t); if ((t.created_at || "") > c.last) c.last = t.created_at; } });
  return [...map.values()].map((c) => ({
    ...c,
    sessions: c.bookings.filter((b) => b.status !== "cancelled").length,
    paid: c.bookings.filter((b) => b.payment_status === "paid").reduce((s, b) => s + (Number(b.deposit_usd) || 0), 0),
    openTickets: c.tickets.filter((t) => !["resolved", "closed"].includes(t.status)).length,
  })).sort((a, b) => (b.last || "").localeCompare(a.last || ""));
}

export function CustomersList({ bookings, tickets, onOpen }) {
  const [q, setQ] = useState("");
  const customers = useMemo(() => buildCustomers(bookings, tickets), [bookings, tickets]);
  const shown = customers.filter((c) => !q || `${c.name} ${c.email} ${c.phone}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-4" data-testid="customers-list">
      <div className="flex flex-wrap items-center gap-3">
        <div className="dg-eyebrow flex items-center gap-1"><Users className="w-3 h-3" /> {customers.length} customer{customers.length === 1 ? "" : "s"}</div>
        <label className="ml-auto relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input className="dg-input pl-9 text-sm w-64" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone" data-testid="customers-search" />
        </label>
      </div>
      {shown.length === 0 && <div className="text-slate-500 text-sm">No customers match.</div>}
      <div className="grid gap-3 md:grid-cols-2">
        {shown.map((c) => (
          <button key={c.email} type="button" onClick={() => onOpen(c.email)} className="dg-card rounded-xl p-4 text-left hover:border-cyan-400 transition-colors" data-testid={`customer-card-${c.email}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-display font-semibold">{c.name || c.email}</div>
                <div className="text-xs text-slate-400 mt-0.5">{c.email}{c.phone ? ` · ${c.phone}` : ""}</div>
              </div>
              <div className="text-right text-xs text-slate-400 whitespace-nowrap">{c.last ? new Date(c.last).toLocaleDateString() : "—"}</div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <span className="dg-chip">{c.sessions} session{c.sessions === 1 ? "" : "s"}</span>
              <span className="dg-chip">${c.paid.toFixed(2)} paid</span>
              <span className="dg-chip">{c.tickets.length} ticket{c.tickets.length === 1 ? "" : "s"}{c.openTickets ? ` · ${c.openTickets} open` : ""}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
